/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from "react";
import AppShell from "../../components/layout/AppShell";
import axios from "axios";
import { toast } from "sonner";
import {
  Image as ImageIcon, MagicWand, UploadSimple, Trash, Copy,
  X, ArrowsClockwise, DownloadSimple, Sparkle, FileImage, FilePdf,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("rescigo_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
const fmtErr = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join("; ");
  if (d && typeof d === "object") return d.msg || JSON.stringify(d);
  return d || e?.message || "Error";
};
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function MediaLibrary() {
  const [assets, setAssets] = useState([]);
  const [source, setSource] = useState("");
  const [preview, setPreview] = useState(null);
  const [genOpen, setGenOpen] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/media", {
        params: source ? { source } : {},
      });
      setAssets(r.data);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); }, [source]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    for (const file of files) {
      const form = new FormData();
      form.append("file", file);
      try {
        await api.post("/admin/media/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(`Uploaded ${file.name}`);
      } catch (err) { toast.error(fmtErr(err)); }
    }
    e.target.value = "";
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this asset? The file will be permanently removed.")) return;
    try {
      await api.delete(`/admin/media/${id}`);
      toast.success("Deleted");
      setPreview(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const copyUrl = (url) => {
    const full = url.startsWith("http") ? url : `${BACKEND}${url}`;
    navigator.clipboard.writeText(full);
    toast.success("URL copied");
  };

  return (
    <AppShell>
      <div className="p-6 md:p-8 space-y-6" data-testid="media-library">
        <header className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-500">Content Management</div>
            <h1 className="font-heading text-3xl mt-1 text-slate-900">Media Library</h1>
            <p className="text-sm text-slate-600 mt-1">AI-generated images, uploaded photos, branded assets, and PDFs.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setGenOpen(true)} data-testid="media-generate-btn"
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-white text-sm font-medium bg-[#2D5A27] hover:bg-[#234920]">
              <MagicWand size={13}/> Generate with AI
            </button>
            <button onClick={() => fileRef.current?.click()} data-testid="media-upload-btn"
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-white text-sm font-medium bg-[#0A2540] hover:bg-[#0E3358]">
              <UploadSimple size={13}/> Upload
            </button>
            <input ref={fileRef} type="file" multiple accept="image/*,.pdf"
                   className="hidden" onChange={upload} data-testid="media-file-input"/>
          </div>
        </header>

        {/* Filters */}
        <div className="flex items-center gap-2">
          {[
            ["", "All sources"],
            ["nano_banana", "AI-generated"],
            ["upload", "Uploaded"],
          ].map(([v, l]) => (
            <button key={v} onClick={() => setSource(v)} data-testid={`media-filter-${v || "all"}`}
                    className={`h-8 px-3 rounded-full text-xs font-medium ${
                      source === v ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                    }`}>
              {l}
            </button>
          ))}
          <div className="ml-auto text-xs text-slate-500">{assets.length} asset(s)</div>
          <button onClick={load} title="Refresh"
                  className="w-8 h-8 rounded-full grid place-items-center hover:bg-slate-100 text-slate-500">
            <ArrowsClockwise size={12}/>
          </button>
        </div>

        {/* Grid */}
        {assets.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border border-slate-200">
            <div className="w-14 h-14 rounded-full bg-slate-100 grid place-items-center mx-auto mb-4">
              <ImageIcon size={22} weight="duotone" className="text-slate-400"/>
            </div>
            <div className="font-heading text-xl text-slate-900">No media yet</div>
            <div className="text-sm text-slate-500 mt-2">Upload your first file or generate one with AI.</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {assets.map(a => (
              <AssetCard key={a.id} asset={a} onClick={() => setPreview(a)}/>
            ))}
          </div>
        )}

        {preview && <PreviewModal asset={preview} onClose={() => setPreview(null)}
                                    onCopy={copyUrl} onDelete={() => remove(preview.id)}/>}
        {genOpen && <GenerateModal onClose={() => setGenOpen(false)} onDone={load}/>}
      </div>
    </AppShell>
  );
}

const AssetCard = ({ asset, onClick }) => {
  const isPdf = asset.mime_type?.includes("pdf");
  const url = asset.url?.startsWith("http") ? asset.url : `${BACKEND}${asset.url}`;
  return (
    <button onClick={onClick} data-testid={`media-card-${asset.id}`}
            className="group relative bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition text-left">
      <div className="aspect-square bg-slate-100 relative overflow-hidden">
        {isPdf ? (
          <div className="w-full h-full grid place-items-center bg-gradient-to-br from-red-50 to-slate-50">
            <FilePdf size={48} weight="duotone" className="text-red-600"/>
          </div>
        ) : (
          <img src={url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition"
               loading="lazy"/>
        )}
        {asset.source === "nano_banana" && (
          <div className="absolute top-2 left-2 bg-[#2D5A27] text-white text-[9px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 rounded inline-flex items-center gap-1">
            <Sparkle size={8} weight="fill"/> AI
          </div>
        )}
      </div>
      <div className="p-2.5">
        <div className="text-[10px] text-slate-500 truncate font-mono">{asset.filename}</div>
        <div className="text-[9px] text-slate-400 mt-0.5 uppercase tracking-[0.15em]">
          {asset.source === "nano_banana" ? "Nano Banana" : "Upload"}
        </div>
      </div>
    </button>
  );
};

const PreviewModal = ({ asset, onClose, onCopy, onDelete }) => {
  const url = asset.url?.startsWith("http") ? asset.url : `${BACKEND}${asset.url}`;
  const isPdf = asset.mime_type?.includes("pdf");
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="media-preview">
      <div className="bg-white rounded-2xl w-full max-w-3xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
              {asset.source === "nano_banana" ? "AI-generated" : "Uploaded"} · {asset.mime_type}
            </div>
            <div className="font-mono text-sm text-slate-800 mt-0.5">{asset.filename}</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded grid place-items-center hover:bg-slate-100">
            <X size={16}/>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto bg-slate-50 p-6 grid place-items-center">
          {isPdf ? (
            <div className="text-center">
              <FilePdf size={64} weight="duotone" className="text-red-600 mx-auto"/>
              <a href={url} target="_blank" rel="noreferrer"
                 className="mt-4 inline-flex items-center gap-2 text-sm text-slate-800 hover:text-slate-900 underline">
                <DownloadSimple size={12}/> Open PDF
              </a>
            </div>
          ) : (
            <img src={url} alt="" className="max-w-full max-h-[60vh] rounded-lg shadow-lg"/>
          )}
        </div>
        {asset.prompt && (
          <div className="px-6 py-3 bg-slate-50 border-t border-slate-200">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">AI prompt</div>
            <div className="text-xs text-slate-700 italic leading-relaxed">"{asset.prompt}"</div>
          </div>
        )}
        <div className="p-4 border-t border-slate-200 flex items-center gap-2">
          <div className="flex-1 text-[11px] text-slate-500 font-mono truncate">{url}</div>
          <button onClick={() => onCopy(asset.url)} data-testid="media-copy-btn"
                  className="h-10 px-3 rounded-md text-sm bg-slate-100 hover:bg-slate-200 text-slate-800 inline-flex items-center gap-1.5">
            <Copy size={12}/> Copy URL
          </button>
          <a href={url} target="_blank" rel="noreferrer"
             className="h-10 px-3 rounded-md text-sm bg-slate-100 hover:bg-slate-200 text-slate-800 inline-flex items-center gap-1.5">
            <DownloadSimple size={12}/> Open
          </a>
          <button onClick={onDelete} data-testid="media-delete-btn"
                  className="h-10 px-3 rounded-md text-sm bg-red-50 hover:bg-red-100 text-red-700 inline-flex items-center gap-1.5">
            <Trash size={12}/> Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const GenerateModal = ({ onClose, onDone }) => {
  const [prompt, setPrompt] = useState("");
  const [aspect, setAspect] = useState("16:9");
  const [busy, setBusy] = useState(false);

  const gen = async (e) => {
    e.preventDefault();
    if (!prompt.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/media/generate", { prompt, aspect });
      toast.success("Image generated & added to library");
      onDone(); onClose();
    } catch (err) { toast.error(fmtErr(err)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#2D5A27]">Nano Banana</div>
            <h2 className="font-heading text-xl text-slate-900 mt-0.5">Generate image with AI</h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded grid place-items-center hover:bg-slate-100">
            <X size={16}/>
          </button>
        </div>
        <form onSubmit={gen} className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-500 block mb-1">Prompt</label>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4}
                      placeholder="e.g. Premium enterprise dashboard visualization showing biomedical waste compliance across Indian states, clean UI with green accents, 16:9 hero image"
                      required autoFocus data-testid="media-gen-prompt"
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:border-slate-800"/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-500 block mb-1">Aspect ratio</label>
            <select value={aspect} onChange={(e) => setAspect(e.target.value)}
                    className="w-full h-10 px-3 border border-slate-300 rounded-md text-sm bg-white outline-none focus:border-slate-800">
              <option value="16:9">16:9 · Hero / Banner</option>
              <option value="4:3">4:3 · Standard</option>
              <option value="1:1">1:1 · Square / Thumbnail</option>
              <option value="3:2">3:2 · Landscape</option>
              <option value="9:16">9:16 · Portrait / Social</option>
            </select>
          </div>
          <div className="bg-slate-50 rounded-lg p-3 text-[11px] text-slate-600">
            Generation typically takes 8–15 seconds. Credits deducted from your Universal Key balance.
          </div>
          <div className="flex items-center gap-2 pt-3 border-t border-slate-200">
            <button type="button" onClick={onClose} className="h-10 px-4 text-sm text-slate-600">Cancel</button>
            <div className="flex-1"/>
            <button type="submit" disabled={busy || !prompt.trim()} data-testid="media-gen-submit"
                    className="h-10 px-5 rounded-md text-white text-sm font-medium inline-flex items-center gap-2 bg-[#2D5A27] hover:bg-[#234920] disabled:opacity-60">
              <MagicWand size={13} weight="fill"/> {busy ? "Generating…" : "Generate"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
